import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

const LS_KEY = "propix3ls_currency_override";

const defaultCtx = {
  currency: "USD",
  symbol: "$",
  fxUsd: 1,
  decimals: 2,
  country: "",
  countryCode: "",
  source: "default",
  ready: false,
  setCurrency: () => {},
  clearOverride: () => {},
  supported: [],
};

const CurrencyContext = createContext(defaultCtx);

export function CurrencyProvider({ children }) {
  const [override, setOverride] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
  });
  const [ctx, setCtx] = useState(defaultCtx);
  const [supported, setSupported] = useState([]);

  const fetchContext = useCallback(async (code) => {
    try {
      const url = code ? `${API}/currency/context?currency=${code}` : `${API}/currency/context`;
      const r = await axios.get(url);
      setCtx({
        currency: r.data.currency,
        symbol: r.data.symbol,
        fxUsd: r.data.fx_usd,
        decimals: r.data.decimals,
        country: r.data.country,
        countryCode: r.data.country_code,
        source: r.data.source,
        usdToGbp: r.data.usd_to_gbp || 0.79,
        ready: true,
        setCurrency: () => {},
        clearOverride: () => {},
        supported: [],
      });
    } catch (e) {
      // Silent fall-back to USD so nothing ever blocks the UI
      setCtx((p) => ({ ...p, ready: true }));
    }
  }, []);

  useEffect(() => { fetchContext(override); }, [override, fetchContext]);

  useEffect(() => {
    axios.get(`${API}/currency/supported`).then((r) => setSupported(r.data || [])).catch(() => {});
  }, []);

  const setCurrency = useCallback((code) => {
    const c = (code || "").toUpperCase();
    if (!c) return;
    try { localStorage.setItem(LS_KEY, c); } catch {}
    setOverride(c);
  }, []);

  const clearOverride = useCallback(() => {
    try { localStorage.removeItem(LS_KEY); } catch {}
    setOverride(null);
  }, []);

  const value = useMemo(() => ({
    ...ctx,
    supported,
    setCurrency,
    clearOverride,
    isOverridden: !!override,
  }), [ctx, supported, setCurrency, clearOverride, override]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

/** Charm-price a converted amount in a currency-decimal-aware way.
 *  Always rounds UP so we never undercharge. */
export function charm(amount, decimals = 2) {
  if (!isFinite(amount) || amount <= 0) return 0;
  if (decimals === 0) {
    // JPY/KRW etc. — round to nearest X99, ≥ amount
    let r = Math.ceil(amount / 100) * 100 - 1;
    while (r < amount) r += 100;
    return r;
  }
  if (decimals === 3) {
    // BHD/KWD etc. — end in .999
    let r = Math.ceil(amount) - 0.001;
    if (r < amount) r = Math.ceil(amount + 0.001) - 0.001;
    return Math.round(r * 1000) / 1000;
  }
  // Default 2-decimal — end in .99
  let r = Math.ceil(amount) - 0.01;
  if (r < amount) r = Math.ceil(amount + 0.01) - 0.01;
  return Math.round(r * 100) / 100;
}

/** Format a charmed amount with the right number of decimals + thousands sep. */
export function formatAmount(amount, decimals = 2) {
  try {
    return amount.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  } catch {
    return amount.toFixed(decimals);
  }
}
